import React, { useState, useImperativeHandle, useEffect, useRef } from "react";

export type TooltipedCompRef = {
  displayTooltip: () => void;
};

export const TooltipPosition = {
  LEFT: "LEFT",
  RIGHT: "RIGHT",
  TOP_LEFT: "TOP_LEFT",
  TOP_CENTER: "TOP_CENTER",
  TOP_RIGHT: "TOP_RIGHT",
  BOTTOM_LEFT: "BOTTOM_LEFT",
  BOTTOM_CENTER: "BOTTOM_CENTER",
  BOTTOM_RIGHT: "BOTTOM_RIGHT",
};

function TooltipedComp(props, ref) {
  const { width, className, time, position, children, tooltip } = props;
  const tooltipWidth = width || 200;
  const timeOut = time || 2000;
  const selectedPosition = position || TooltipPosition.RIGHT;
  const addedClasses = className || "";
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [tooltipPositionClasses, setTooltipPositionClasses] =
    useState<string>("");
  const [tooltipAlignmentClasses, setTooltipAlignmentClasses] =
    useState<string>("");
  const divRef = useRef<HTMLDivElement>(null);

  const displayTooltip = () => {
    setShowTooltip(true);

    setTimeout(() => {
      setShowTooltip(false);
    }, timeOut);
  };

  useImperativeHandle(ref, () => ({ displayTooltip }));

  const visibility = () => (showTooltip ? "visible" : "hidden");

  useEffect(() => {
    let margin = 0;
    let hMargin = 0;
    let vMargin = 0;
    const contentDiv = divRef.current;

    if (!contentDiv) return;

    switch (selectedPosition) {
      case TooltipPosition.LEFT:
        if (contentDiv) {
          hMargin = contentDiv.clientWidth + 8;
        }
        setTooltipPositionClasses(`justify-self-end mr-[${hMargin}px]`);
        setTooltipAlignmentClasses("flex-row-reverse items-center");
        break;
      case TooltipPosition.TOP_LEFT:
        if (contentDiv) {
          vMargin = contentDiv.clientHeight + 8;
          margin = -(tooltipWidth - contentDiv.clientWidth);
        }
        setTooltipPositionClasses(`ml-[${margin}px] mb-[${vMargin}px]`);
        setTooltipAlignmentClasses("flex-col-reverse");
        break;
      case TooltipPosition.BOTTOM_LEFT:
        if (contentDiv) {
          vMargin = contentDiv.clientHeight + 8;
          margin = -(tooltipWidth - contentDiv.clientWidth);
        }
        setTooltipPositionClasses(`ml-[${margin}px] mt-[${vMargin}px]`);
        setTooltipAlignmentClasses("flex-col");
        break;
      case TooltipPosition.TOP_CENTER:
        if (contentDiv) {
          vMargin = contentDiv.clientHeight + 8;
          margin = -((tooltipWidth - contentDiv.clientWidth) / 2);
        }
        setTooltipPositionClasses(`ml-[${margin}px] mb-[${vMargin}px]`);
        setTooltipAlignmentClasses("flex-col-reverse");
        break;
      case TooltipPosition.BOTTOM_CENTER:
        if (contentDiv) {
          vMargin = contentDiv.clientHeight + 8;
          margin = -((tooltipWidth - contentDiv.clientWidth) / 2);
        }
        setTooltipPositionClasses(`ml-[${margin}px] mt-[${vMargin}px]`);
        setTooltipAlignmentClasses("flex-col");
        break;
      case TooltipPosition.BOTTOM_RIGHT:
        if (contentDiv) {
          vMargin = contentDiv.clientHeight + 8;
        }
        setTooltipPositionClasses(`mt-[${vMargin}px]`);
        setTooltipAlignmentClasses("flex-col");
        break;
      case TooltipPosition.TOP_RIGHT:
        if (contentDiv) {
          vMargin = contentDiv.clientHeight + 8;
        }
        setTooltipPositionClasses(`mb-[${vMargin}px]`);
        setTooltipAlignmentClasses("flex-col-reverse");
        break;
      case TooltipPosition.RIGHT:
      default:
        if (contentDiv) {
          hMargin = contentDiv.clientWidth + 8;
        }
        setTooltipPositionClasses(`ml-[${hMargin}px]`);
        setTooltipAlignmentClasses("flex-row items-center");
        break;
    }
  }, []);

  return (
    <div className={`flex ${tooltipAlignmentClasses} relative ${addedClasses}`}>
      <div ref={divRef} className="flex">
        {children}
      </div>
      <div
        className={`${visibility()} absolute w-[${tooltipWidth}px] ${tooltipPositionClasses} text-white rounded-md bodyText p-[10px] pr-[34px] bg-gray-700`}
      >
        {tooltip}
      </div>
    </div>
  );
}

export default React.forwardRef(TooltipedComp);
